import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  approveWithheldReward,
  denyWithheldReward,
  getWithheldRewards,
  type WithheldReward,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";

export default function HireRewardsPage() {
  const [items, setItems] = useState<WithheldReward[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    getWithheldRewards()
      .then((data) => setItems(data.items))
      .catch(() => setErr("Could not load withheld rewards"));
  };

  useEffect(() => {
    load();
  }, []);

  const onApprove = async (id: string) => {
    setBusyId(id);
    setErr(null);
    try {
      await approveWithheldReward(id);
      load();
    } catch {
      setErr("Could not approve reward");
    } finally {
      setBusyId(null);
    }
  };

  const onDeny = async (id: string) => {
    setBusyId(id);
    setErr(null);
    try {
      await denyWithheldReward(id);
      load();
    } catch {
      setErr("Could not deny reward");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Withheld referral rewards ({items.length})</h1>
      {err && <p className="text-danger">{err}</p>}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">Referrer</th>
              <th className="px-4 py-3 font-medium">Referee</th>
              <th className="px-4 py-3 font-medium">Credits</th>
              <th className="px-4 py-3 font-medium">Reason</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link to={`/users/${row.referrerUserId}`} className="text-accent hover:underline">
                    {row.referrerEmail}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/users/${row.refereeUserId}`} className="text-accent hover:underline">
                    {row.refereeEmail}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-fg">
                  {row.referrerCredits} / {row.refereeCredits}
                </td>
                <td className="px-4 py-3 text-muted-fg">{row.withheldReason ?? "-"}</td>
                <td className="px-4 py-3 text-muted-fg">{formatDate(row.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={busyId === row.id}
                      onClick={() => void onApprove(row.id)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === row.id}
                      onClick={() => void onDeny(row.id)}
                    >
                      Deny
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !err && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-fg">
                  No withheld rewards
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
